'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';

const supabase = createClient();

interface TwilioAlert {
  id: string;
  destinatario: string;
  messaggio: string;
  stato: string;
  twilio_sid: string;
  errore: string;
  tentativi: number;
  inviato_at: string | null;
  created_at: string;
  notifica_id: string | null;
}

interface AlertForm {
  to: string;
  message: string;
}

interface TwilioStatus {
  configured: boolean;
  fields: {
    TWILIO_ACCOUNT_SID: string | null;
    TWILIO_AUTH_TOKEN: string | null;
    TWILIO_PHONE_NUMBER: string | null;
  };
  missing: string[];
}

const STATO_CONFIG: Record<string, { label: string; bg: string; text: string; icon: string }> = {
  inviato: { label: 'Inviato', bg: 'bg-green-500/15', text: 'text-green-400', icon: 'CheckCircleIcon' },
  fallito: { label: 'Fallito', bg: 'bg-red-500/15', text: 'text-red-400', icon: 'XCircleIcon' },
  in_coda: { label: 'In Coda', bg: 'bg-amber-500/15', text: 'text-amber-400', icon: 'ClockIcon' },
  pending: { label: 'In Attesa', bg: 'bg-sky-500/15', text: 'text-sky-400', icon: 'ClockIcon' },
};

export default function TwilioAlertsContent() {
  const [alerts, setAlerts] = useState<TwilioAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState<AlertForm>({ to: '', message: '' });
  const [sendResult, setSendResult] = useState<{ success: boolean; message: string } | null>(null);
  const [activeAlarms, setActiveAlarms] = useState<{ id: string; titolo: string; priorita: string }[]>([]);
  const [selectedAlarmId, setSelectedAlarmId] = useState('');
  const [twilioStatus, setTwilioStatus] = useState<TwilioStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  const fetchTwilioStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await fetch('/api/notifications/sms-status');
      const data = await res.json();
      setTwilioStatus(data);
    } catch {
      setTwilioStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('sms_queue')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      setAlerts((data || []) as TwilioAlert[]);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActiveAlarms = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('allarmi')
        .select('id, titolo, priorita')
        .in('stato', ['attivo', 'riconosciuto', 'in_gestione'])
        .order('created_at', { ascending: false })
        .limit(20);
      setActiveAlarms((data || []) as { id: string; titolo: string; priorita: string }[]);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchTwilioStatus();
    fetchAlerts();
    fetchActiveAlarms();

    const channel = supabase
      .channel('sms_queue_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'sms_queue' }, fetchAlerts)
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchAlerts, fetchActiveAlarms, fetchTwilioStatus]);

  const handleAlarmSelect = (alarmId: string) => {
    setSelectedAlarmId(alarmId);
    const alarm = activeAlarms.find(a => a.id === alarmId);
    if (alarm) {
      setForm(prev => ({
        ...prev,
        message: `[GestioneLab Alert] ${alarm.priorita.toUpperCase()}: ${alarm.titolo}. Verificare immediatamente.`,
      }));
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.to || !form.message) return;
    setSending(true);
    setSendResult(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const res = await fetch('/api/notifications/send-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          to: form.to,
          message: form.message,
          notifica_id: selectedAlarmId || null,
        }),
      });

      const data = await res.json();

      if (data.success) {
        setSendResult({ success: true, message: `✅ SMS inviato con successo! SID: ${data.messageSid}` });
        setForm({ to: '', message: '' });
        setSelectedAlarmId('');
        await fetchAlerts();
        await fetchTwilioStatus();
      } else if (data.warning) {
        setSendResult({
          success: false,
          message: `⚠️ Twilio non configurato. Aggiungi le credenziali reali nel file .env (vedi istruzioni sotto).`,
        });
      } else {
        // Show the full Twilio error for diagnosis
        setSendResult({
          success: false,
          message: data.error || 'Errore sconosciuto durante l\'invio SMS',
        });
      }
    } catch (err: unknown) {
      setSendResult({
        success: false,
        message: `Errore di rete: ${err instanceof Error ? err.message : 'Connessione fallita'}`,
      });
    } finally {
      setSending(false);
    }
  };

  const formatDate = (d: string | null) =>
    d ? new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

  const stats = {
    total: alerts.length,
    inviati: alerts.filter(a => a.stato === 'inviato').length,
    falliti: alerts.filter(a => a.stato === 'fallito').length,
  };

  const isConfigured = twilioStatus?.configured === true;

  return (
    <div className="p-6 space-y-6">
      <Breadcrumb items={[{ label: 'Allarmi', href: '/allarmi' }, { label: 'Alert Twilio SMS' }]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Icon name="DevicePhoneMobileIcon" size={28} className="text-sky-400" />
            Alert Twilio SMS
          </h1>
          <p className="text-muted-color text-sm mt-1">Invia notifiche SMS via Twilio per allarmi critici</p>
        </div>
        <button
          onClick={() => { fetchAlerts(); fetchTwilioStatus(); }}
          className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 text-sky-400 rounded-lg hover:bg-sky-500/20 transition-colors text-sm font-semibold"
        >
          <Icon name="ArrowPathIcon" size={16} />
          Aggiorna
        </button>
      </div>

      {/* ── Twilio Config Status Banner ── */}
      {!statusLoading && (
        <div className={`rounded-xl border p-4 ${isConfigured ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
          <div className="flex items-start gap-3">
            <Icon
              name={isConfigured ? 'CheckCircleIcon' : 'ExclamationTriangleIcon'}
              size={20}
              className={isConfigured ? 'text-green-400 flex-shrink-0 mt-0.5' : 'text-red-400 flex-shrink-0 mt-0.5'}
            />
            <div className="flex-1 min-w-0">
              {isConfigured ? (
                <div>
                  <p className="text-sm font-semibold text-green-400">Twilio configurato correttamente</p>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-green-300/80">
                    <span>Account SID: <code className="font-mono">{twilioStatus?.fields.TWILIO_ACCOUNT_SID}</code></span>
                    <span>Auth Token: <code className="font-mono">***</code></span>
                    <span>Da: <code className="font-mono">{twilioStatus?.fields.TWILIO_PHONE_NUMBER}</code></span>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm font-bold text-red-400">⛔ Twilio NON configurato — l&apos;invio SMS non funzionerà</p>
                  <p className="text-xs text-red-300/80 mt-1">
                    Le seguenti variabili d&apos;ambiente contengono ancora valori placeholder:
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {twilioStatus?.missing.map(key => (
                      <code key={key} className="text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded font-mono">{key}</code>
                    ))}
                  </div>
                  <div className="mt-3 p-3 bg-black/30 rounded-lg text-xs text-slate-300 space-y-1.5 font-mono">
                    <p className="text-slate-400 font-sans font-semibold not-italic mb-2">📋 Aggiungi nel file <code>.env</code> del progetto:</p>
                    <p>TWILIO_ACCOUNT_SID=<span className="text-amber-300">ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx</span></p>
                    <p>TWILIO_AUTH_TOKEN=<span className="text-amber-300">xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx</span></p>
                    <p>TWILIO_PHONE_NUMBER=<span className="text-amber-300">+39XXXXXXXXXX</span></p>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    Trovi queste credenziali su{' '}
                    <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-sky-400 underline">
                      console.twilio.com
                    </a>{' '}
                    → Dashboard principale (Account SID e Auth Token) e Phone Numbers → Manage.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'SMS Totali', value: stats.total, icon: 'ChatBubbleLeftRightIcon', color: 'text-sky-400', bg: 'bg-sky-500/10' },
          { label: 'Inviati', value: stats.inviati, icon: 'CheckCircleIcon', color: 'text-green-400', bg: 'bg-green-500/10' },
          { label: 'Falliti', value: stats.falliti, icon: 'XCircleIcon', color: 'text-red-400', bg: 'bg-red-500/10' },
        ].map(({ label, value, icon, color, bg }) => (
          <div key={label} className="bg-surface border border-border rounded-xl p-4 flex items-center gap-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${bg}`}>
              <Icon name={icon as any} size={20} className={color} />
            </div>
            <div>
              <div className={`text-2xl font-bold ${color}`}>{value}</div>
              <div className="text-xs text-muted-color">{label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Send SMS Form */}
        <div className="bg-surface border border-border rounded-xl p-5 space-y-4">
          <h2 className="text-base font-bold text-white flex items-center gap-2">
            <Icon name="PaperAirplaneIcon" size={18} className="text-sky-400" />
            Invia Alert SMS
          </h2>

          <form onSubmit={handleSend} className="space-y-4">
            {/* Link to active alarm (optional) */}
            <div>
              <label className="block text-xs font-semibold text-muted-color mb-1.5 uppercase tracking-wide">
                Collega ad Allarme Attivo (opzionale)
              </label>
              <select
                value={selectedAlarmId}
                onChange={e => handleAlarmSelect(e.target.value)}
                className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500"
              >
                <option value="">— Nessun allarme collegato —</option>
                {activeAlarms.map(a => (
                  <option key={a.id} value={a.id}>
                    [{a.priorita.toUpperCase()}] {a.titolo}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-color mb-1.5 uppercase tracking-wide">
                Numero Destinatario
              </label>
              <input
                type="tel"
                value={form.to}
                onChange={e => setForm(prev => ({ ...prev, to: e.target.value }))}
                placeholder="+39 333 1234567"
                required
                className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-sm text-white placeholder-muted-color focus:outline-none focus:border-sky-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-color mb-1.5 uppercase tracking-wide">
                Messaggio
              </label>
              <textarea
                value={form.message}
                onChange={e => setForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Testo del messaggio SMS..."
                required
                rows={4}
                maxLength={160}
                className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2.5 text-sm text-white placeholder-muted-color focus:outline-none focus:border-sky-500 resize-none"
              />
              <div className="text-right text-xs text-muted-color mt-1">{form.message.length}/160</div>
            </div>

            {sendResult && (
              <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${sendResult.success ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                <Icon name={sendResult.success ? 'CheckCircleIcon' : 'ExclamationCircleIcon'} size={16} className="flex-shrink-0 mt-0.5" />
                <span className="break-all">{sendResult.message}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={sending || !form.to || !form.message || !isConfigured}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-sky-500 hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors"
            >
              {sending ? (
                <>
                  <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                  Invio in corso...
                </>
              ) : (
                <>
                  <Icon name="PaperAirplaneIcon" size={16} />
                  {isConfigured ? 'Invia SMS' : 'Twilio non configurato'}
                </>
              )}
            </button>
          </form>
        </div>

        {/* SMS Log */}
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <Icon name="QueueListIcon" size={18} className="text-sky-400" />
              Log SMS Inviati
            </h2>
            <span className="text-xs text-muted-color">{alerts.length} messaggi</span>
          </div>

          {loading ? (
            <div className="p-8 flex justify-center">
              <svg className="animate-spin w-6 h-6 text-sky-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : alerts.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Icon name="ChatBubbleLeftRightIcon" size={32} className="text-muted-color" />
              <p className="text-sm text-muted-color">Nessun SMS inviato</p>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-96 divide-y divide-border/50">
              {alerts.map(alert => {
                const cfg = STATO_CONFIG[alert.stato] || STATO_CONFIG['pending'];
                return (
                  <div key={alert.id} className="px-5 py-3.5 hover:bg-surface-elevated/30 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon name={cfg.icon as any} size={15} className={cfg.text} />
                        <span className="text-sm font-semibold text-white truncate font-mono">{alert.destinatario}</span>
                      </div>
                      <span className={`flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
                        {cfg.label}
                      </span>
                    </div>
                    <p className="text-xs text-secondary-color mt-1 line-clamp-2">{alert.messaggio}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs text-muted-color">{formatDate(alert.created_at)}</span>
                      {alert.twilio_sid && (
                        <span className="text-xs text-muted-color font-mono truncate max-w-32">{alert.twilio_sid}</span>
                      )}
                    </div>
                    {alert.errore && (
                      <p className="text-xs text-red-400 mt-1 break-all">{alert.errore}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
